import { useCallback, useEffect, useState } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { useCompleteDepartureTask } from "@features/vessel-call/vessel-call-status/hooks/useCompleteDepartureTask";
import { zodResolver } from "@hookform/resolvers/zod";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { TFunction } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LANG_BASE, TEST_ID_BASE } from "../DepartureStatus/DepartureStatus";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
  taskId: string;
};

const INTERNAL_DOCUMENT_KEY = "internalRejectDocument";
const EXTERNAL_DOCUMENT_KEY = "externalRejectDocument";

const documentSchema = z.object({
  name: z.string(),
  url: z.string(),
  type: z.string(),
});

const getRejectSchema = (t: TFunction) =>
  z.object({
    rejectionReason: z
      .string()
      .trim()
      .min(
        1,
        t("form-validation.required", {
          label: t(`${LANG_BASE}.rejection-reason`),
        }),
      )
      .max(
        500,
        t("form-validation.max-length", {
          label: t(`${LANG_BASE}.rejection-reason`),
          max: 500,
        }),
      ),
    internalNotes: z
      .string()
      .max(
        500,
        t("form-validation.max-length", { label: t(`${LANG_BASE}.internal-notes`), max: 500 }),
      )
      .optional(),
    [INTERNAL_DOCUMENT_KEY]: z.array(documentSchema).optional(),
    [EXTERNAL_DOCUMENT_KEY]: z.array(documentSchema).optional(),
  });

const RejectSideDrawer = ({ isOpen, onClose, onSuccess, onError, taskId }: Props) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { clearFilesForKey, setFilesForKey, filesByKey } = useUploadStore();

  const internalFile = filesByKey[INTERNAL_DOCUMENT_KEY] || [];
  const externalFile = filesByKey[EXTERNAL_DOCUMENT_KEY] || [];

  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const rejectSchema = getRejectSchema(t);
  type RejectFormValues = z.infer<typeof rejectSchema>;

  const form = useForm<RejectFormValues>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(rejectSchema),
    shouldUnregister: true,
  });

  const handleClose = () => {
    form.reset();
    clearFilesForKey(INTERNAL_DOCUMENT_KEY);
    clearFilesForKey(EXTERNAL_DOCUMENT_KEY);
    onClose();
  };

  const { completeTask } = useCompleteDepartureTask({
    handleSuccess: () => {
      handleClose();
      onSuccess();
    },
    handleError: (error) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
      handleClose();
      onError();
    },
  });

  const onValid = async (data: RejectFormValues) => {
    const { internalNotes, rejectionReason, internalRejectDocument, externalRejectDocument } = data;
    const internalDoc = internalRejectDocument?.[0];
    const externalDoc = externalRejectDocument?.[0];

    try {
      setIsSubmitting(true);
      await completeTask({
        taskId,
        params: {
          status: "REJECTED",
          internalNotes: internalNotes,
          rejectionReason: rejectionReason,
          internalAttachmentsName: internalDoc?.name,
          internalAttachmentsUrl: internalDoc?.url,
          rejectionAttachmentsName: externalDoc?.name,
          rejectionAttachmentsUrl: externalDoc?.url,
        },
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  useEffect(() => {
    clearFilesForKey(INTERNAL_DOCUMENT_KEY);
    clearFilesForKey(EXTERNAL_DOCUMENT_KEY);
  }, []);

  const mutationFn = ({ file, documentType }: { file: File; documentType: string }) =>
    runTransaction(
      {
        module: "vessel_call",
        screen: "RejectDeparture",
        action: "upload_departure_document",
        entityType: "departure_document",
      },
      async (transactionId) => {
        const payloadForSasToken = {
          prefix: SasTokenPrefix.VESSEL_CALL,
          contentType: file.type || null,
          privilege: SasTokenPrivilege.WRITE,
          blobUrl: null,
        };

        const { blobUrl, sasToken } = await getSasToken(
          payloadForSasToken,
          ...(transactionId ? [transactionId] : []),
        );

        const [uploadResult] = await uploadFileToAzureBlob({
          file,
          blobUrl,
          sasToken,
          certificateType: documentType,
        });

        const documentUrl = uploadResult?.documentUrl ?? blobUrl;
        return { documentUrl, documentType };
      },
    );

  const externalDocumentUpload = useMutation({ mutationFn });
  const internalDocumentUpload = useMutation({ mutationFn });

  const handleExternalDocumentUpload = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;

      externalDocumentUpload.mutate(
        { file, documentType: "EXTERNAL_NOTES" },
        {
          onSuccess: ({ documentUrl }) => {
            form.setValue(EXTERNAL_DOCUMENT_KEY, [
              { url: documentUrl, name: file.name, type: file.type },
            ]);
            setFilesForKey(EXTERNAL_DOCUMENT_KEY, [
              {
                name: file.name,
                url: documentUrl,
              },
            ]);
          },
          onError: () => {
            clearFilesForKey(EXTERNAL_DOCUMENT_KEY);
            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
          },
        },
      );
    },
    [clearFilesForKey, externalDocumentUpload, form, notify, setFilesForKey, t],
  );

  const handleInternalDocumentUpload = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;

      internalDocumentUpload.mutate(
        { file, documentType: "INTERNAL_NOTES" },
        {
          onSuccess: ({ documentUrl }) => {
            form.setValue(INTERNAL_DOCUMENT_KEY, [
              { url: documentUrl, name: file.name, type: file.type },
            ]);
            setFilesForKey(INTERNAL_DOCUMENT_KEY, [
              {
                name: file.name,
                url: documentUrl,
              },
            ]);
          },
          onError: () => {
            clearFilesForKey(INTERNAL_DOCUMENT_KEY);
            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
          },
        },
      );
    },
    [clearFilesForKey, internalDocumentUpload, form, notify, setFilesForKey, t],
  );

  const handleRemoveFile = useCallback(
    (ATTACHMENT_KEY: typeof INTERNAL_DOCUMENT_KEY | typeof EXTERNAL_DOCUMENT_KEY) => {
      form.setValue(ATTACHMENT_KEY, undefined);
      clearFilesForKey(ATTACHMENT_KEY);
    },
    [clearFilesForKey, form],
  );

  return (
    <div data-testid="departure-reject-sidedrawer">
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={handleClose}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          keepMounted={false}
          title={t(`${LANG_BASE}.reject-title`)}
          description={t(`${LANG_BASE}.reject-description`)}
          submitLabel={t(`${LANG_BASE}.reject-button`)}
        >
          <div className="w-[400px] p-6 flex flex-col gap-6">
            <FormItem
              name="rejectionReason"
              type="textarea"
              label={t(`${LANG_BASE}.rejection-reason`)}
              testId={`${TEST_ID_BASE}-rejection-reason`}
              minRows={4}
              maxChars={500}
            />
            {externalFile[0] ? (
              <div className="MuiFormControl-root MuiFormControl-fullWidth css-kjxwpk-MuiFormControl-root">
                <CustomFormLabel sx={{ mb: 1 }}>
                  {t(`${LANG_BASE}.external-document`)}
                </CustomFormLabel>
                <SingleFile
                  testId={`${TEST_ID_BASE}-external-document-0`}
                  file={externalFile[0]}
                  fieldName={externalFile[0].name}
                  currentFileIndex={0}
                  setFiles={() => handleRemoveFile(EXTERNAL_DOCUMENT_KEY)}
                  isLoading={externalDocumentUpload.isPending}
                />
              </div>
            ) : (
              <FormItem
                name={EXTERNAL_DOCUMENT_KEY}
                type="file"
                label={t(`${LANG_BASE}.external-document`)}
                testId={`${TEST_ID_BASE}-external-document`}
                allowedFileExtensions={allowedSideDrawerExtensions}
                uploadConditions={profile}
                maxFiles={1}
                onUpload={(_, files) => handleExternalDocumentUpload(files)}
                setFiles={(next) => setFilesForKey(EXTERNAL_DOCUMENT_KEY, next as UploadEntry[])}
                files={externalFile}
              />
            )}
            <FormItem
              name="internalNotes"
              type="textarea"
              label={t(`${LANG_BASE}.internal-notes`)}
              testId={`${TEST_ID_BASE}-internal-notes`}
              minRows={4}
              maxChars={500}
            />
            {internalFile[0] ? (
              <div className="MuiFormControl-root MuiFormControl-fullWidth css-kjxwpk-MuiFormControl-root">
                <CustomFormLabel sx={{ mb: 1 }}>
                  {t(`${LANG_BASE}.internal-document`)}
                </CustomFormLabel>
                <SingleFile
                  testId={`${TEST_ID_BASE}-internal-document-0`}
                  file={internalFile[0]}
                  fieldName={internalFile[0].name}
                  currentFileIndex={0}
                  setFiles={() => handleRemoveFile(INTERNAL_DOCUMENT_KEY)}
                  isLoading={internalDocumentUpload.isPending}
                />
              </div>
            ) : (
              <FormItem
                name={INTERNAL_DOCUMENT_KEY}
                type="file"
                label={t(`${LANG_BASE}.internal-document`)}
                testId={`${TEST_ID_BASE}-internal-document`}
                allowedFileExtensions={allowedSideDrawerExtensions}
                uploadConditions={profile}
                maxFiles={1}
                onUpload={(_, files) => handleInternalDocumentUpload(files)}
                setFiles={(next) => setFilesForKey(INTERNAL_DOCUMENT_KEY, next as UploadEntry[])}
                files={internalFile}
              />
            )}
          </div>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default RejectSideDrawer;
